import React from 'react';
import { Box, IconButton, Typography, CircularProgress } from '@mui/material';
import { Close } from '@mui/icons-material';
import styled from '@emotion/styled';

const WidgetContainer = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
}));

const WidgetHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: '60px',
}));

// The header has a fixed height and the content area gets whatever is left, so a title allowed to
// wrap silently eats a line of the widget — worst in edit mode, where the remove button squeezes
// it. It gives way to the actions and truncates instead.
const WidgetTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
}));

const WidgetContent = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    flex: 1,
    overflow: 'auto',
}));

const LoadingContainer = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minHeight: '200px',
});

const ErrorContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minHeight: '200px',
    color: theme.palette.error.main,
    textAlign: 'center',
}));

const HeaderActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexShrink: 0,
}));

/**
 * BaseWidget - Wrapper component for all dashboard widgets
 * 
 * Provides:
 * - Consistent styling
 * - Header with title and close button
 * - Loading state
 * - Error state
 * - Scrollable content area
 */
const BaseWidget = ({ 
    title, 
    widgetId,
    onRemove, 
    isLoading = false, 
    error = null, 
    children,
    actions = null,
}) => {
    const handleRemove = (e) => {
        e.stopPropagation();
        if (onRemove) {
            onRemove(widgetId);
        }
    };

    return (
        <WidgetContainer>
            <WidgetHeader>
                <WidgetTitle variant="h6" title={typeof title === 'string' ? title : undefined}>
                    {title}
                </WidgetTitle>
                <HeaderActions>
                    {actions}
                    {onRemove && (
                        <IconButton
                            size="small"
                            onClick={handleRemove}
                            aria-label="Remove widget"
                            className="widget-no-drag"
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    )}
                </HeaderActions>
            </WidgetHeader>
            <WidgetContent>
                {isLoading ? (
                    <LoadingContainer>
                        <CircularProgress />
                    </LoadingContainer>
                ) : error ? (
                    <ErrorContainer>
                        <Typography variant="body2">
                            {error}
                        </Typography>
                    </ErrorContainer>
                ) : (
                    children
                )}
            </WidgetContent>
        </WidgetContainer>
    );
};

export default BaseWidget;
